/**
 * سقفِ دعوت برای کسی که هنوز دوستی را تایید نکرده (WHOSUP-204).
 *
 * از امروز می‌شود کسی را دعوت کرد که درخواستِ دوستی بینتان هنوز باز است —
 * و برای اینکه این در، درِ اسپم نشود، تا لحظه‌ی تایید حداکثر سه دعوت.
 *
 * حرفِ آخر را **دیتابیس** می‌زند (مایگریشن 0141: تریگرِ
 * `cap_unaccepted_invites` و تابعِ `invite_allowance`). این فایل دو کارِ
 * دیگر می‌کند، هر دو سمتِ کاربر:
 *
 *   ۱. فرم پیش از فرستادن بداند چند تا مانده، تا ردیفِ پرشده خاکستری شود و
 *      کاربر اصلاً به خطا نخورد.
 *   ۲. وقتی خطا خورد (مثلاً دو دستگاه هم‌زمان)، بفهمد این همان خطاست و
 *      **دلیلش** را به زبانِ خودِ کاربر بگوید — نه یک خطای خامِ پستگرس.
 *
 * عمداً خالص و بدونِ ایمپورت، تا با `npm test` تست شود.
 */

/**
 * همان عددی که `public.invite_cap()` در مایگریشن 0141 می‌گوید.
 *
 * ⚠ این‌جا فقط برای **متنِ پیام** است («بیشتر از ۳ دعوت نمی‌شود»). عددی که
 * واقعاً جلوی درج را می‌گیرد آن‌جاست؛ اگر آن عوض شد، این هم عوض شود.
 */
export const UNACCEPTED_INVITE_CAP = 3;

/** قراردادِ `chain_limit` و `invite_allowance`: منفی یعنی بی‌سقف. */
export const INVITES_UNLIMITED = -1;

/**
 * این آدم را می‌شود دعوت کرد؟
 *
 * `undefined` یعنی «هنوز نمی‌دانیم» — کوئریِ سهمیه نیامده یا شکست خورده. در
 * آن حالت جلوی کاربر گرفته نمی‌شود: نرده در دیتابیس است و یک کوئریِ ناموفق
 * نباید فرمِ دعوت را قفل کند.
 */
export const canInvite = (remaining: number | undefined): boolean => remaining !== 0;

/** کلیدِ ترجمه و عددش، برای خطِ دومِ ردیفِ یک دوستِ هنوز-تاییدنکرده. */
export interface InviteCapHint {
  key: 'pendingFriendInvitesLeft' | 'pendingFriendCapFull';
  n: number;
}

/**
 * خطِ دومِ ردیف در انتخابگرِ دعوت.
 *
 * `null` یعنی چیزی برای گفتن نیست: یا دوستیِ تاییدشده است (بی‌سقف)، یا
 * هنوز سهمیه را نمی‌دانیم. نشان دادنِ «۳ دعوت مانده» به دوستِ تاییدشده
 * همان‌قدر گمراه‌کننده است که پنهان کردنِ «پر شده» از بقیه.
 */
export function inviteCapHint(remaining: number | undefined): InviteCapHint | null {
  if (remaining === undefined || remaining < 0) return null;
  return remaining > 0
    ? { key: 'pendingFriendInvitesLeft', n: remaining }
    : { key: 'pendingFriendCapFull', n: UNACCEPTED_INVITE_CAP };
}

/**
 * این خطا همان سقف است؟
 *
 * تریگر پیامِ ثابتِ `invite_cap_reached` را raise می‌کند و PostgREST آن را
 * در `message` می‌آورد. رشته‌ی ثابت و نه کدِ خطا: کدِ `P0001` مالِ هر
 * `raise exception`ِ دیگری هم هست.
 */
export function isInviteCapError(err: unknown): boolean {
  if (!err) return false;
  const e = err as { message?: unknown; details?: unknown };
  const text = `${typeof e.message === 'string' ? e.message : ''} `
    + `${typeof e.details === 'string' ? e.details : ''}`;
  return text.includes('invite_cap_reached');
}
